import React from 'react';
import { openWhatsApp, BATCH_DURATION, SHOW_SUMMER_PROMO } from '../constants';
import { useNavigate } from 'react-router-dom';
import SummerOffer from '../components/SummerOffer';

/* ─────────────────────────────────────────
   REUSABLE SUB-COMPONENTS
───────────────────────────────────────── */

const StatCard = ({ num, label }) => (
  <div style={{
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 16,
    padding: '18px 20px',
    textAlign: 'center',
  }}>
    <div style={{ fontSize: 26, fontWeight: 900, color: '#F0B429' }}>{num}</div>
    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600, marginTop: 4 }}>{label}</div>
  </div>
);

const FeatureCard = ({ icon, title, desc }) => (
  <div className="card" style={{ textAlign: 'center', padding: '32px 24px' }}>
    <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
    <h3 style={{ color: 'var(--navy)', fontWeight: 800, fontSize: 17, marginBottom: 8 }}>{title}</h3>
    <p style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.65 }}>{desc}</p>
  </div>
);

const OutcomeCard = ({ icon, title, desc }) => (
  <div className="card" style={{ borderTop: '4px solid var(--gold)' }}>
    <div style={{ fontSize: 32, marginBottom: 10 }}>{icon}</div>
    <h3 style={{ color: 'var(--navy)', fontWeight: 800, fontSize: 16, marginBottom: 8 }}>{title}</h3>
    <p style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.65 }}>{desc}</p>
  </div>
);

const CareerCard = ({ icon, title, desc, highlight }) => (
  <div style={{
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 20,
    padding: 28,
  }}>
    <div style={{ fontSize: 32, marginBottom: 10 }}>{icon}</div>
    <h3 style={{ color: 'var(--gold)', fontWeight: 800, fontSize: 16, marginBottom: 8 }}>{title}</h3>
    <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.65 }}>{desc}</p>
    {highlight && (
      <div style={{
        marginTop: 14,
        background: 'rgba(240,180,41,0.15)',
        border: '1px solid rgba(240,180,41,0.3)',
        borderRadius: 10,
        padding: '8px 14px',
        color: '#F0B429',
        fontWeight: 800,
        fontSize: 14,
      }}>
        {highlight}
      </div>
    )}
  </div>
);

const TestimonialCard = ({ name, cls, quote, stars }) => (
  <div className="card" style={{ padding: 28 }}>
    <div style={{ fontSize: 14, marginBottom: 8 }}>{stars}</div>
    <p style={{ color: 'var(--gray)', fontSize: 15, lineHeight: 1.7, marginBottom: 18, fontStyle: 'italic' }}>
      "{quote}"
    </p>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--navy), var(--crimson))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontWeight: 900, fontSize: 15,
      }}>
        {name[0]}
      </div>
      <div>
        <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 15 }}>{name}</div>
        <div style={{ color: 'var(--gray)', fontSize: 13 }}>{cls}</div>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   "WHO IS THIS FOR" ROW ITEM
───────────────────────────────────────── */
const ForCard = ({ icon, title, desc }) => (
  <div style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: 18,
    background: '#fff',
    border: '1.5px solid #e8ecf4',
    borderRadius: 18,
    padding: '22px 24px',
    boxShadow: '0 2px 12px rgba(13,32,69,0.06)',
  }}>
    <div style={{
      fontSize: 30,
      background: 'rgba(240,180,41,0.12)',
      borderRadius: 14,
      padding: '10px 12px',
      flexShrink: 0,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 16, marginBottom: 4 }}>{title}</div>
      <div style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.6 }}>{desc}</div>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   BUILD ITEM (inside course cards)
───────────────────────────────────────── */
const BuildItem = ({ text }) => (
  <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
    <span style={{
      background: 'rgba(240,180,41,0.25)',
      color: '#F0B429',
      fontWeight: 900,
      fontSize: 12,
      borderRadius: 6,
      padding: '2px 8px',
      marginTop: 1,
      flexShrink: 0,
    }}>BUILD</span>
    <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>{text}</span>
  </div>
);

/* ─────────────────────────────────────────
   WHATSAPP FLOATING BUTTON
───────────────────────────────────────── */
const WhatsAppFloat = ({ onClick }) => (
  <button
    onClick={onClick}
    title="Chat on WhatsApp"
    style={{
      position: 'fixed',
      bottom: 28,
      right: 24,
      zIndex: 9999,
      width: 60,
      height: 60,
      borderRadius: '50%',
      background: '#25D366',
      border: 'none',
      boxShadow: '0 4px 20px rgba(37,211,102,0.45)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}
    onMouseEnter={e => {
      e.currentTarget.style.transform = 'scale(1.12)';
      e.currentTarget.style.boxShadow = '0 6px 28px rgba(37,211,102,0.6)';
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,211,102,0.45)';
    }}
  >
    <img src="/whatsapp.png" alt="WhatsApp" style={{ height: 32, width: 32 }} />
  </button>
);

/* ─────────────────────────────────────────
   SECTION CTA STRIP  (reusable inline nudge)
───────────────────────────────────────── */
const SectionCTA = ({ label = 'Register for Course', onBook }) => (
  <div style={{ textAlign: 'center', marginTop: 48 }}>
    <button
      className="btn-primary pulse-anim"
      style={{ fontSize: 16, padding: '15px 36px' }}
      onClick={onBook}
    >
      {label}
    </button>
    <p style={{ color: 'var(--gray)', fontSize: 13, marginTop: 10 }}>
      No fees. No commitment. Just one free class.
    </p>
  </div>
);

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
export default function HomePage() {

  const navigate = useNavigate();

  const registerForCourse = () => navigate('/register');

  return (
    <div>

      {/* ── Urgency Banner ── */}
      <div className="urgency-bar" style={{ letterSpacing: 0.4, fontWeight: 700 }}>
        🔥 Limited Seats Available — Next Batch Filling Fast. Register for Course Today!
      </div>

      {SHOW_SUMMER_PROMO && <SummerOffer/>}

      {/* ════════════════════════════════════
          1. HERO
      ════════════════════════════════════ */}
      <section style={{
        background: 'linear-gradient(135deg, #0d2045 0%, #1B3A6B 55%, #2A5298 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '100px 24px 72px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative blobs */}
        <div style={{ position: 'absolute', width: 520, height: 520, background: '#F0B429', borderRadius: '50%', opacity: 0.055, top: -120, right: -140 }} />
        <div style={{ position: 'absolute', width: 320, height: 320, background: '#C41E3A', borderRadius: '50%', opacity: 0.07, bottom: -70, left: -90 }} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="fade-up">

            {/* Eyebrow badge */}
            <div className="badge" style={{
              background: 'rgba(240,180,41,0.18)',
              color: '#F0B429',
              border: '1.5px solid rgba(240,180,41,0.35)',
            }}>
              🚀 For Students After 10th / Intermediate / Degree
            </div>

            {/* Primary headline — outcome focused */}
            <h1 className="serif" style={{
              fontSize: 'clamp(32px, 5.5vw, 60px)',
              color: '#fff',
              lineHeight: 1.13,
              margin: '18px 0 18px',
              fontWeight: 800,
            }}>
              Coding &amp; AI Skills<br />
              <span style={{ color: '#F0B429' }}>for Students After 10th</span>
            </h1>

            {/* Subheadline — parent-friendly, outcome-focused */}
            <p style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: 'clamp(15px, 2.5vw, 19px)',
              lineHeight: 1.75,
              marginBottom: 14,
              maxWidth: 560,
            }}>
              Your child will <strong style={{ color: '#F0B429' }}>build real projects</strong>, gain tech confidence,
              and be ready for India's fastest-growing career — in just 6–8 weeks.
            </p>

            {/* Urgency nudge line */}
            <p style={{
              color: '#F0B429',
              fontWeight: 700,
              fontSize: 14,
              marginBottom: 36,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              ⚡ Only 8 seats per batch · <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 400 }}>Batch starting soon</span>
            </p>

            {/* Primary CTAs */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 52 }}>
              <button
                className="btn-primary pulse-anim"
                style={{ fontSize: 16, padding: '15px 32px' }}
                onClick={registerForCourse}
              >
                📅 Register for Course
              </button>
              <button
                onClick={() => navigate('/courses')} 
                style={{
                  background: 'transparent',
                  color: '#fff',
                  padding: '14px 30px',
                  border: '2.5px solid rgba(255,255,255,0.45)',
                  borderRadius: 50,
                  fontWeight: 800,
                  fontSize: 15,
                  cursor: 'pointer',
                  fontFamily: 'Nunito, sans-serif',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                View Courses →
              </button>
            </div>

            {/* Stat strip */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 12,
              maxWidth: 520,
            }}>
              {[
                ['50+', 'Students Trained'],
                ['95%', 'Satisfaction Rate'],
                ['2', 'Courses Available'],
                [BATCH_DURATION, 'Course Duration'],
              ].map(([n, l]) => (
                <StatCard key={l} num={n} label={l} />
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          2. WHO IS THIS FOR  (NEW)
      ════════════════════════════════════ */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge">✅ Is This Right for My Child?</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Who Is VRK Tech Academy For?
            </h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center', maxWidth: 540 }}>
              No experience needed. If your child has a laptop and curiosity, we'll handle the rest.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 18,
            maxWidth: 900,
            margin: '0 auto',
          }}>
            <ForCard
              icon="🎓"
              title="Students After 10th, Intermediate or Degree"
              desc="Perfect for students who want to build a strong tech foundation early — before or alongside college."
            />
            <ForCard
              icon="🔰"
              title="Complete Beginners — Zero Experience Needed"
              desc="We start from the very basics. If your child has never written a line of code, this is exactly where to start."
            />
            <ForCard
              icon="💻"
              title="Anyone with a Laptop & Internet"
              desc="Our live online sessions can be attended from home. No commute, no stress — just learning."
            />
            <ForCard
              icon="👨‍👩‍👧"
              title="Parents Who Want to Future-Proof Their Child"
              desc="Tech skills are the most in-demand skills in India today. Give your child a real advantage."
            />
          </div>

          {/* Quick reassurance line for parents */}
          <div style={{
            marginTop: 36,
            background: 'linear-gradient(135deg, #f4f7fb, #eef2fa)',
            border: '1.5px solid #d6e0f5',
            borderRadius: 18,
            padding: '20px 28px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            maxWidth: 700,
            margin: '36px auto 0',
          }}>
            <span style={{ fontSize: 32 }}>💡</span>
            <p style={{ color: 'var(--navy)', fontSize: 15, lineHeight: 1.6, margin: 0 }}>
              <strong>For Parents:</strong> No technical background required from you either.
              Our instructor explains everything in simple terms — for both students and parents.
            </p>
          </div>

          <SectionCTA label="Register for Course" onBook={registerForCourse} />
        </div>
      </section>

      {/* ════════════════════════════════════
          3. WHY CHOOSE VRK
      ════════════════════════════════════ */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge">✨ Why Choose Us</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>Why Parents Trust VRK Tech Academy</h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              We don't just teach coding. We build confident, capable students — one project at a time.
            </p>
          </div>
          <div className="grid-3">
            {[
              ['🎯', 'Starts from Zero', 'No prior knowledge needed. We begin from basics and go step by step.'],
              ['🛠️', 'Learn by Building', 'Students build 5–8 real projects — not just theory exercises.'],
              ['👥', 'Small Batches Only', 'Max 8 students per batch. Every student gets personal attention.'],
              ['🏆', 'Instructor-Led Sessions', 'Live classes with your dedicated trainer — not pre-recorded videos.'],
              ['💻', 'Online from Home', 'Attend live from home. All sessions are recorded for revision.'],
              ['📜', 'Completion Certificate', 'Receive a certificate to add to your resume or portfolio.'],
            ].map(([icon, title, desc]) => (
              <FeatureCard key={title} icon={icon} title={title} desc={desc} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          4. COURSES — WHAT STUDENTS BUILD
      ════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge">📚 Our Courses</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>What Will Your Child Build?</h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              Not just theory — your child will finish the course with real, working projects they built themselves.
            </p>
          </div>

          <div className="grid-2">

            {/* ── Python + AI Card ── */}
            <div style={{
              background: 'linear-gradient(135deg, #1B3A6B, #2A5298)',
              borderRadius: 24,
              padding: 40,
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: -24, right: -24, width: 110, height: 110, background: 'rgba(240,180,41,0.13)', borderRadius: '50%' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <img src="/python.png" alt="Python" style={{ height: 44, width: 44, margin: 10 }} />
                <div>
                  <h3 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>Python + AI Course</h3>
                  <div style={{ color: '#F0B429', fontWeight: 700, fontSize: 13, marginTop: 3 }}>
                    6–8 Weeks · Beginner Friendly
                  </div>
                </div>
              </div>

              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.78)', marginBottom: 20, lineHeight: 1.6 }}>
                We start from the very basics — what a program is — and build up to writing real programs,
                solving logic puzzles, and exploring AI tools. Your child will leave confident and capable.
              </p>

              {/* What they LEARN */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 700, marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>What they'll learn</div>
                {[
                  'Python programming from absolute scratch',
                  'Logic building & problem solving',
                  'Intro to AI tools & automation',
                ].map(p => (
                  <div key={p} style={{ display: 'flex', gap: 8, marginBottom: 7, fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>
                    <span style={{ color: '#F0B429', fontWeight: 900 }}>✓</span>{p}
                  </div>
                ))}
              </div>

              {/* What they BUILD */}
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 700, marginBottom: 10, letterSpacing: 1, textTransform: 'uppercase' }}>What they'll build (6 real projects)</div>
                <BuildItem text="A Calculator App — their first working Python program" />
                <BuildItem text="A Number Guessing Game with logic & conditions" />
                <BuildItem text="A Student Grade Tracker" />
                <BuildItem text="A Simple AI Chatbot using Python" />
                <BuildItem text="A File Organizer Script for automation" />
                <BuildItem text="A Data Analyser — intro to real-world Python use" />
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button className="btn-gold" onClick={() => navigate('/python')}>Course Details →</button>
                <button className="btn-primary" onClick={registerForCourse}>Register for Course</button>
              </div>
            </div>

            {/* ── Web Development Card ── */}
            <div style={{
              background: 'linear-gradient(135deg, #C41E3A, #e84363)',
              borderRadius: 24,
              padding: 40,
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: -24, right: -24, width: 110, height: 110, background: 'rgba(240,180,41,0.13)', borderRadius: '50%' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 44 }}>🌐</span>
                <div>
                  <h3 style={{ fontSize: 22, fontWeight: 900, margin: 0 }}>Web Development Course</h3>
                  <div style={{ color: '#F0B429', fontWeight: 700, fontSize: 13, marginTop: 3 }}>
                    6–8 Weeks · Build Real Websites
                  </div>
                </div>
              </div>

              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.78)', marginBottom: 20, lineHeight: 1.6 }}>
                Learn the three pillars of the web — HTML, CSS, and JavaScript — and build real websites
                they can publish online and show to anyone. By week 8 they'll have a portfolio website live on the internet.
              </p>

              {/* What they LEARN */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 700, marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>What they'll learn</div>
                {[
                  'HTML5, CSS3 & JavaScript from scratch',
                  'Responsive & mobile-friendly design',
                  'Build a personal portfolio website live on the internet',
                ].map(p => (
                  <div key={p} style={{ display: 'flex', gap: 8, marginBottom: 7, fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>
                    <span style={{ color: '#F0B429', fontWeight: 900 }}>✓</span>{p}
                  </div>
                ))}
              </div>

              {/* What they BUILD */}
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 700, marginBottom: 10, letterSpacing: 1, textTransform: 'uppercase' }}>What they'll build (6 real projects)</div>
                <BuildItem text="A Personal Portfolio website — ready to share online" />
                <BuildItem text="A Contact Form Page with real interactivity" />
                <BuildItem text="A Product Showcase page with layout & design" />
                <BuildItem text="A Blog Layout — responsive on mobile & desktop" />
                <BuildItem text="An Interactive Quiz App with JavaScript" />
                <BuildItem text="An Image Gallery with clean CSS design" />
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button className="btn-gold" onClick={() => navigate('/webdev')}>Course Details →</button>
                <button style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  padding: '13px 22px',
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderRadius: 50,
                  fontWeight: 800,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'Nunito, sans-serif',
                }} onClick={registerForCourse}>
                  Register for Course
                </button>
              </div>
            </div>

          </div>

          <SectionCTA label="Register for Course" onBook={registerForCourse} />
        </div>
      </section>

      {/* ════════════════════════════════════
          5. STUDENT OUTCOMES
      ════════════════════════════════════ */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge">🎯 Student Outcomes</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>What Your Child Will Achieve</h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              By the end of the course, your child won't just know coding — they'll feel confident using it.
            </p>
          </div>
          <div className="grid-4">
            {[
              ['📁', '6 Real Projects Per Course', 'A portfolio of actual programs and websites they built themselves — calculator, chatbot, quiz app, and more.'],
              ['💪', 'Strong Tech Foundation', 'Core skills that apply to any IT career: software engineer, web developer, AI roles.'],
              ['🧠', 'Logical Thinking', 'The ability to break complex problems into simple steps — a skill for life.'],
              ['🚀', 'Career Confidence', 'Ready to apply for internships, college projects, or even freelancing gigs.'],
            ].map(([icon, title, desc]) => (
              <OutcomeCard key={title} icon={icon} title={title} desc={desc} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          6. CAREER AWARENESS  (PARENT-FOCUSED)
      ════════════════════════════════════ */}
      <section className="section" style={{ background: 'var(--navy)' }}>
        <div className="container">

          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge" style={{
              background: 'rgba(240,180,41,0.18)',
              color: '#F0B429',
              border: '1.5px solid rgba(240,180,41,0.35)',
            }}>
              💼 For Parents — Why This Matters
            </div>
            <h2 className="section-title" style={{ textAlign: 'center', color: '#fff' }}>
              What Is a Software Job —<br />
              <span style={{ color: '#F0B429' }}>And Why Should Your Child Learn Coding?</span>
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.72)',
              fontSize: 17,
              textAlign: 'center',
              maxWidth: 620,
              margin: '0 auto',
              lineHeight: 1.75,
            }}>
              A software job means writing programs that run websites, apps, AI systems, and more.
              These jobs are <strong style={{ color: '#F0B429' }}>high-paying, stable, and in massive demand</strong> across India and the world.
            </p>
          </div>

          {/* Career stat cards */}
          <div className="grid-4" style={{ marginBottom: 40 }}>
            <CareerCard
              icon="💰"
              title="Great Starting Salaries"
              desc="Entry-level software jobs in India pay well — even fresh graduates earn significantly more than most other fields."
              highlight="₹3–10 LPA for beginners"
            />
            <CareerCard
              icon="📈"
              title="Massive Job Demand"
              desc="India needs millions of software engineers every year. Companies like TCS, Infosys, Google, Amazon are always hiring."
              highlight="1M+ jobs added yearly"
            />
            <CareerCard
              icon="🌍"
              title="Work from Anywhere"
              desc="Software engineers can work remotely, freelance globally, or move to any city in India. The freedom is unmatched."
              highlight="Remote & flexible careers"
            />
            <CareerCard
              icon="🤖"
              title="AI Is the Future"
              desc="Artificial Intelligence is changing every industry. People who understand how to use and build AI tools will always lead."
              highlight="AI skills = future-proof career"
            />
          </div>

          {/* Salary growth visual strip */}
          <div style={{
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 20,
            padding: '28px 32px',
            marginBottom: 40,
          }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ color: '#F0B429', fontWeight: 800, fontSize: 15, marginBottom: 4 }}>
                💡 Salary Growth — What Coders Earn in India
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                These are real industry figures. The earlier your child starts, the faster they grow.
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 16,
            }}>
              {[
                { level: 'Fresher (0–1 yr)', range: '₹3–6 LPA', color: '#64b5f6' },
                { level: 'Junior Dev (2–3 yr)', range: '₹6–12 LPA', color: '#81c784' },
                { level: 'Mid-Level (4–6 yr)', range: '₹12–25 LPA', color: '#F0B429' },
                { level: 'Senior / Lead', range: '₹25–50 LPA+', color: '#ff8a65' },
              ].map(({ level, range, color }) => (
                <div key={level} style={{
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: 14,
                  padding: '16px 20px',
                  textAlign: 'center',
                  border: `1px solid ${color}33`,
                }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color }}>{range}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>{level}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button
              className="btn-gold"
              style={{ fontSize: 16, padding: '15px 36px' }}
              onClick={registerForCourse}
            >
              🚀 Help Your Child Start This Journey Today
            </button>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 10 }}>
              First class includes demo experience before fee payment.
            </p>
          </div>

        </div>
      </section>

      {/* ════════════════════════════════════
          7. TRUST — FOUNDER & TEACHING STYLE  (NEW)
      ════════════════════════════════════ */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge">🤝 Meet Your Instructor</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Taught by a Real Practitioner, Not Just a Tutor
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 32,
            alignItems: 'center',
            maxWidth: 900,
            margin: '0 auto',
          }}>

            {/* Instructor card */}
            <div style={{
              background: 'linear-gradient(135deg, #0d2045, #1B3A6B)',
              borderRadius: 24,
              padding: 36,
              color: '#fff',
              textAlign: 'center',
            }}>
              <img src="/admin_pic.png" alt="Instructor" style={{ width: 120, height: 120, borderRadius: '50%', marginBottom: 16 }} />
              <h3 style={{ fontSize: 20, fontWeight: 900, marginBottom: 4 }}>Venkata Ramakrishna Tagarampudi</h3>
              <h3 style={{ fontSize: 16, fontWeight: 900, marginBottom: 4 }}>Founder, VRK Tech Academy</h3>
              <div style={{ color: '#F0B429', fontSize: 13, fontWeight: 700, marginBottom: 18 }}>
                Software Professional · Coding Educator
              </div>
              <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 1.7 }}>
                With hands-on industry experience, our founder started VRK Tech Academy with one goal —
                to make quality coding education accessible to every student in India,
                regardless of their background.
              </p>
            </div>

            {/* Teaching promise list */}
            <div>
              <h3 style={{ color: 'var(--navy)', fontWeight: 800, fontSize: 20, marginBottom: 24 }}>
                Our Teaching Promise
              </h3>
              {[
                ['🎯', 'Beginner-First Approach', 'We assume zero prior knowledge. No student is ever made to feel behind.'],
                ['🛠️', '100% Practical Learning', 'Every concept is immediately applied to a real mini-project — no boring lectures.'],
                ['🔁', 'Doubts Welcome, Always', 'Every session ends with a dedicated Q&A. No question is too small.'],
                ['📏', 'Small Batch — Big Attention', 'We limit every batch to 8 students so you always get personal guidance.'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{
                  display: 'flex',
                  gap: 14,
                  marginBottom: 20,
                  alignItems: 'flex-start',
                }}>
                  <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{icon}</span>
                  <div>
                    <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 15, marginBottom: 2 }}>{title}</div>
                    <div style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.55 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

          </div>

          <SectionCTA label="Register for Course" onBook={registerForCourse} />
        </div>
      </section>

      {/* ════════════════════════════════════
          8. TESTIMONIALS
      ════════════════════════════════════ */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="badge">💬 Student Stories</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>Real Students, Real Results</h2>
            <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
              These students had zero coding experience before joining VRK Tech Academy.
            </p>
          </div>
          <div className="grid-3">
            {[
              ['Rahul K.', 'Intermediate Student', 'After joining VRK Tech Academy, I built my first website in just 6 weeks. The instructor explained everything so patiently — I never felt lost even once!', '⭐⭐⭐⭐⭐'],
              ['Priya M.', '10th Pass Student', 'I had zero coding knowledge. Now I can write Python programs and I even built a small quiz game. Best decision of my life!', '⭐⭐⭐⭐⭐'],
              ['Arjun S.', 'Degree Student', 'The small batch size meant I always got personal attention when I was stuck. The instructor would not move forward until everyone understood.', '⭐⭐⭐⭐⭐'],
            ].map(([name, cls, quote, stars]) => (
              <TestimonialCard key={name} name={name} cls={cls} quote={quote} stars={stars} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          9. FINAL CTA — STRONG CLOSE
      ════════════════════════════════════ */}
      <section style={{
        background: 'linear-gradient(135deg, #C41E3A, #e84363)',
        padding: '80px 24px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>

          {/* Urgency badge */}
          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: '#fff',
            fontWeight: 800,
            fontSize: 13,
            padding: '6px 18px',
            borderRadius: 50,
            marginBottom: 20,
            letterSpacing: 0.5,
          }}>
            ⚡ Limited Seats — Next Batch Starting Soon
          </div>

          <h2 className="serif" style={{
            color: '#fff',
            fontSize: 'clamp(28px, 5vw, 50px)',
            fontWeight: 800,
            marginBottom: 16,
            lineHeight: 1.2,
          }}>
            Start Your Child's Tech Journey Today
          </h2>

          <p style={{
            color: 'rgba(255,255,255,0.9)',
            fontSize: 'clamp(15px, 2.5vw, 19px)',
            marginBottom: 14,
            lineHeight: 1.7,
            maxWidth: 560,
            margin: '0 auto 14px',
          }}>
            Register for course now. First class is included as demo.
            Fee payment can be completed in the first 3 classes.
          </p>

          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, marginBottom: 40 }}>
            Batch size is 8 students only. Once seats fill, we open the next batch - don't wait.
          </p>

          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn-gold pulse-anim"
              style={{ fontSize: 17, padding: '16px 40px' }}
              onClick={registerForCourse}
            >
              📅 Register for Course
            </button>
            <button
              className="btn-whatsapp"
              style={{
                fontSize: 17,
                padding: '16px 36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
              onClick={() => openWhatsApp()}
            >
              <img src="/whatsapp.png" alt="WhatsApp" style={{ height: 26 }} />
              WhatsApp Us
            </button>
          </div>

          {/* Reassurance line */}
          <p style={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: 13,
            marginTop: 28,
          }}>
            🔒 No spam. We'll only contact you about your registration.
          </p>

        </div>
      </section>

      {/* ── WhatsApp Floating Button ── */}
      <WhatsAppFloat onClick={() => openWhatsApp()} />

    </div>
  );
}
